import { useState } from "react";
import { X, Send, Mic, Upload, Link, Paperclip } from "lucide-react";
import WhatsMentionsPopup from "./WhatsMentionsPopup";
import { useWhatsAppGroupMembers } from "../../../../client/hooks/useWhatsAppData";

// 02.7-settings.md §5/paso b -- extraído de WhatsPanel.jsx. Puramente presentacional: el envío de
// media/shortlink (mutaciones de React Query) vive en el container, este componente solo llama
// `onSendMedia`/`onUploadShortlink` sin argumentos (el `droppedFile` que necesitan también vive
// en el container). Carga el send-guard/burbuja-pendiente de §4 -- ya resueltos en el container,
// este componente solo refleja `sending` para deshabilitar el textarea/botón.
export default function WhatsMessageComposer({
  chatJid, isGroup,
  isDragging, onDragOver, onDragLeave, onDrop,
  droppedFiles = [], onCancelDroppedFile,
  newMessage, onMessageChange, onSubmit, onAttachClick, sending, inputRef,
  replyingTo, onCancelReply, uploadProgress,
}) {
  const hasFiles = droppedFiles && droppedFiles.length > 0;
  
  const membersQuery = useWhatsAppGroupMembers(isGroup ? chatJid : null);
  const [mentionState, setMentionState] = useState({ show: false, query: '', startIndex: -1 });

  const handleMessageChange = (e) => {
    let val = e.target.value;
    val = val.replace(/(^|\n)-\s?/g, "$1• ");
    
    // Check for mention trigger
    const cursor = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursor);
    const match = textBeforeCursor.match(/@([a-zA-Z0-9]*)$/);
    if (match && isGroup) {
      setMentionState({ show: true, query: match[1], startIndex: cursor - match[1].length - 1 });
    } else {
      setMentionState(prev => prev.show ? { show: false, query: '', startIndex: -1 } : prev);
    }
    
    onMessageChange(val);
  };

  const handleMentionSelect = (num) => {
    const before = newMessage.slice(0, mentionState.startIndex);
    const after = newMessage.slice(mentionState.startIndex + mentionState.query.length + 1); // +1 for the @
    const val = `${before}@${num} ${after}`;
    onMessageChange(val);
    setMentionState({ show: false, query: '', startIndex: -1 });
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  return (
    <div
      className={`whats-input-area ${isDragging ? 'is-dragging' : ''}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, 'attachment')}
      style={{
        padding: "var(--space-3)",
        backgroundColor: "#f0f2f5",
        borderTop: "1px solid #d1d7db",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "var(--space-2)",
        transition: "all 0.2s ease",
        position: "relative"
      }}
    >
      {isDragging && (
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          zIndex: 10,
          display: "flex",
          padding: "var(--space-3)",
          gap: "var(--space-3)",
          backdropFilter: "blur(2px)"
        }}>
          <div 
            style={{ flex: "7", border: "2px dashed #4caf50", borderRadius: "var(--radius-md)", backgroundColor: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", color: "#2e7d32", fontWeight: "bold", fontSize: "15px" }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, 'attachment'); }}
          >
            <Upload size={20} style={{ marginRight: '8px' }} /> Enviar como Adjunto
          </div>
          <div 
            style={{ flex: "3", border: "2px dashed #3b82f6", borderRadius: "var(--radius-md)", backgroundColor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#1d4ed8", fontWeight: "bold", fontSize: "14px", textAlign: "center", flexDirection: "column", gap: "4px" }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, 'link'); }}
          >
            <Link size={18} /> Crear Link Público
          </div>
        </div>
      )}
      {/* integración main→dev, epic B2 -- barra de "respondiendo a" */}
      {replyingTo && (
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", background: "#fff", borderLeft: "4px solid #00a884", borderRadius: "var(--radius-sm)", padding: "6px 10px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "12px", fontWeight: "bold", color: "#00a884" }}>
              {replyingTo.from_me ? "Tú" : (replyingTo.sender_jid?.split("@")[0] || "Contacto")}
            </div>
            <div style={{ fontSize: "13px", color: "#54656f", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
              {replyingTo.text_content || "Mensaje multimedia"}
            </div>
          </div>
          <button onClick={onCancelReply} title="Cancelar respuesta" style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444" }}>
            <X size={18} />
          </button>
        </div>
      )}
      {/* integración main→dev, epic B2 -- barra de progreso de subida */}
      {uploadProgress > 0 && uploadProgress < 100 && (
        <div style={{ height: "4px", background: "#d1d7db", borderRadius: "2px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${uploadProgress}%`, background: "#00a884", transition: "width 0.15s ease" }} />
        </div>
      )}
      <form
        style={{ width: "100%", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}
        onSubmit={onSubmit}
      >
        {hasFiles && (
          <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "var(--space-2)", paddingBottom: "var(--space-2)" }}>
            {droppedFiles.map((df, index) => (
              <div key={index} style={{ flex: 1, backgroundColor: "#fff", padding: "var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid #ddd", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", overflow: "hidden" }}>
                  {df.intent === 'link' ? <Link size={18} color="#3b82f6" style={{ flexShrink: 0 }} /> : <Upload size={18} color="#4caf50" style={{ flexShrink: 0 }} />}
                  <span style={{ fontSize: "14px", fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {df.file.name} ({(df.file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button type="button" onClick={() => onCancelDroppedFile(index)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444" }}>
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ 
          position: "relative",
          display: "flex", 
          width: "100%", 
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "var(--radius-md)",
          padding: "8px 12px",
          alignItems: "flex-end",
          gap: "var(--space-2)"
        }}>
          {mentionState.show && isGroup && (
            <WhatsMentionsPopup
              members={membersQuery.data?.members || []}
              query={mentionState.query}
              onSelect={handleMentionSelect}
            />
          )}
          <textarea
            ref={inputRef}
            placeholder={hasFiles ? "Añadir un comentario (opcional)" : "Escribe un mensaje"}
            value={newMessage}
            onChange={handleMessageChange}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (e.shiftKey || e.metaKey || e.ctrlKey) {
                  // Allow new line
                } else {
                  e.preventDefault();
                  onSubmit(e);
                }
              }
            }}
            disabled={sending}
            rows={1}
            style={{
              flex: 1,
              resize: "none",
              overflowY: "auto",
              minHeight: "24px",
              maxHeight: "120px",
              lineHeight: "1.4",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              fontFamily: "var(--font-main, inherit)",
              fontSize: "16px",
              padding: 0,
              margin: 0,
              alignSelf: "center",
              boxShadow: "none"
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <button
              type="button"
              onClick={onAttachClick}
              disabled={sending}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#54656f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px"
              }}
            >
              <Paperclip size={20} />
            </button>
            <button
              type="submit"
              className="whats-send-btn"
              disabled={(!newMessage.trim() && !hasFiles) || sending}
              style={{
                padding: "6px",
                background: (newMessage.trim() || hasFiles) ? "#00a884" : "transparent",
                color: (newMessage.trim() || hasFiles) ? "#fff" : "#54656f",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "none",
                opacity: (newMessage.trim() || hasFiles) ? 1 : 0.5,
              }}
            >
              {(newMessage.trim() || hasFiles) ? <Send size={18} /> : <Mic size={18} />}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
